import { usePathname, useRouter } from 'expo-router';
import { useEffect, useRef } from 'react';
import { BackHandler, Platform } from 'react-native';

/**
 * Android hardware-back guard (Wave 63, user report: "back sometimes
 * minimizes the app instead of going back").
 *
 * Why it happened: several navigations use router.replace (goTo) to
 * keep history tidy, so a screen reached that way has an EMPTY back
 * stack — Android's default for back-at-root is "move task to
 * background," which reads as the app randomly minimizing.
 *
 * The guard: when back is pressed and the router can't pop, send the
 * user to Today instead — unless they're already somewhere that IS a
 * natural exit point (Today itself, or the onboarding landing page),
 * where minimizing is the expected OS behavior.
 */
const EXIT_PATHS = new Set(['/', '/index', '/start']);

export function useAndroidBackGuard(): void {
  const router = useRouter();
  const pathname = usePathname();
  // Ref so the (single) listener always sees the current path without
  // re-subscribing on every navigation.
  const pathRef = useRef(pathname);
  pathRef.current = pathname;

  useEffect(() => {
    if (Platform.OS !== 'android') return;
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      // A real stack exists — let expo-router pop it normally.
      if (router.canGoBack()) return false;
      // At a natural exit point — allow the OS default (minimize).
      if (EXIT_PATHS.has(pathRef.current)) return false;
      // Empty stack anywhere else (replace-navigation landed us here):
      // "back" should mean "toward home," never "vanish the app."
      router.replace('/(tabs)' as never);
      return true;
    });
    return () => sub.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
